import { useState, useRef } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import { javascript } from "@codemirror/lang-javascript";
import { html as htmlLang } from "@codemirror/lang-html";
import { css as cssLang } from "@codemirror/lang-css";
import { sql } from "@codemirror/lang-sql";
import { oneDark } from "@codemirror/theme-one-dark";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CODING } from "@/constants/testIds";
import { toast } from "sonner";
import { Sparkles, Play, Wand2, CheckCircle2, AlertTriangle, Lightbulb, TrendingUp, Loader2, Terminal as TerminalIcon } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";

// Load Pyodide from CDN on demand (only when learner clicks Run on Python)
const PYODIDE_VERSION = "0.26.4";
const PYODIDE_INDEX_URL = `https://cdn.jsdelivr.net/pyodide/v${PYODIDE_VERSION}/full/`;

async function loadPyodideOnce(setStage) {
  if (window.__lanexPyodide) return window.__lanexPyodide;
  if (window.__lanexPyodideLoading) return window.__lanexPyodideLoading;
  window.__lanexPyodideLoading = (async () => {
    setStage("script");
    // Inject loader script
    if (!document.querySelector(`script[data-pyodide]`)) {
      await new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = `${PYODIDE_INDEX_URL}pyodide.js`;
        s.async = true;
        s.dataset.pyodide = "1";
        s.onload = resolve;
        s.onerror = () => reject(new Error("pyodide-script-failed"));
        document.head.appendChild(s);
      });
    }
    setStage("runtime");
    const py = await window.loadPyodide({ indexURL: PYODIDE_INDEX_URL });
    window.__lanexPyodide = py;
    return py;
  })();
  return window.__lanexPyodideLoading;
}

const LANGS = [
  { key: "python", label: "Python", ext: python },
  { key: "javascript", label: "JavaScript", ext: javascript },
  { key: "html", label: "HTML", ext: htmlLang },
  { key: "css", label: "CSS", ext: cssLang },
  { key: "sql", label: "SQL", ext: sql },
  { key: "matlab", label: "MATLAB", ext: null }, // Mentor teaching mode only
];

const STARTERS = {
  python: "# Write Python here\ndef greet(name):\n    return f'Hello, {name}!'\n\nprint(greet('LaneX'))\n",
  javascript: "// Write JavaScript here\nfunction greet(name) {\n  return `Hello, ${name}!`;\n}\nconsole.log(greet('LaneX'));\n",
  html: "<!doctype html>\n<html>\n  <body>\n    <h1>Hello LaneX</h1>\n  </body>\n</html>\n",
  css: "body {\n  background: #050505;\n  color: #00E5FF;\n  font-family: 'Manrope', sans-serif;\n}\n",
  sql: "-- Write SQL here\nSELECT name, level FROM learners WHERE level > 3 ORDER BY level DESC;\n",
  matlab: "% Write MATLAB code here — your mentor teaches you interactively\nA = [1 2; 3 4];\ndet_A = det(A);\n",
};

export default function CodingLabPage() {
  const { theme } = useTheme();
  const [lang, setLang] = useState("python");
  const [code, setCode] = useState(STARTERS.python);
  const [prompt, setPrompt] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [review, setReview] = useState(null);
  const [hintsOnly, setHintsOnly] = useState(true);
  const [preview, setPreview] = useState(null); // for HTML/CSS/JS run
  const [pyOutput, setPyOutput] = useState(null); // Pyodide stdout
  const [pyRunning, setPyRunning] = useState(false);
  const [pyStage, setPyStage] = useState(null); // 'script' | 'runtime' | null
  const pyOutRef = useRef([]);

  const active = LANGS.find((l) => l.key === lang);

  const changeLang = (k) => {
    setLang(k);
    setCode(STARTERS[k] || "");
    setReview(null);
    setPreview(null);
    setPyOutput(null);
  };

  const doReview = async () => {
    setReviewing(true);
    setReview(null);
    try {
      const { data } = await api.post("/coding/review", {
        language: lang,
        code,
        prompt: prompt || null,
        request_hints_only: hintsOnly,
      });
      setReview(data.review);
      if (data.new_badges?.length) toast.success(`New badge: ${data.new_badges.join(", ")}`);
      if (data.xp?.awarded_xp) toast.success(`+${data.xp.awarded_xp} XP`);
    } catch { toast.error("Review failed"); }
    finally { setReviewing(false); }
  };

  const generateExercise = async () => {
    try {
      const { data } = await api.post("/coding/exercise", { language: lang, difficulty: "medium" });
      setPrompt(data.prompt || "");
      setCode(data.starter_code || STARTERS[lang]);
      setReview(null);
      toast.success(`New exercise: ${data.title || "Practice"}`);
    } catch { toast.error("Could not generate exercise"); }
  };

  const runPython = async () => {
    setPyRunning(true);
    setPreview(null);
    setReview(null);
    pyOutRef.current = [];
    setPyOutput({ status: "loading", lines: [], error: null });
    try {
      const py = await loadPyodideOnce(setPyStage);
      setPyStage("running");
      // Route stdout/stderr into our capture buffer
      py.setStdout({ batched: (s) => pyOutRef.current.push(s) });
      py.setStderr({ batched: (s) => pyOutRef.current.push(s) });
      let error = null;
      try {
        await py.runPythonAsync(code);
      } catch (e) {
        error = String(e && e.message ? e.message : e);
      }
      const lines = pyOutRef.current.join("").split("\n").filter((l, i, arr) => i < arr.length - 1 || l.length);
      setPyOutput({ status: "done", lines, error });
      setPyStage(null);
    } catch (e) {
      setPyOutput({ status: "error", lines: [], error: "Failed to load Python runtime. Check your connection and try again." });
      setPyStage(null);
    } finally {
      setPyRunning(false);
    }
  };

  const runPreview = () => {
    if (lang === "html") {
      setPreview(code);
    } else if (lang === "css") {
      setPreview(`<!doctype html><html><head><style>${code}</style></head><body><h1>Preview</h1><p>This is a paragraph.</p><button>Button</button></body></html>`);
    } else if (lang === "javascript") {
      // Upgraded sandbox: structured console capture + error handling + postMessage bridge.
      const escaped = code.replace(/<\/script>/g, "<\\/script>");
      const html = `<!doctype html><html><head><style>
        body{background:#0a0a0a;color:#e5e5e5;font:12px 'JetBrains Mono',monospace;margin:0;padding:14px}
        .row{padding:2px 0;white-space:pre-wrap;word-break:break-word}
        .log{color:#e5e5e5}.info{color:#22d3ee}.warn{color:#facc15}.error{color:#f87171}.result{color:#a3e635}
        .tag{opacity:.55;margin-right:6px;font-size:10px;text-transform:uppercase;letter-spacing:.08em}
      </style></head><body><div id="out"></div><script>
        (function(){
          const out=document.getElementById('out');
          const write=(kind,args)=>{
            const row=document.createElement('div');
            row.className='row '+kind;
            const tag=document.createElement('span');tag.className='tag';tag.textContent=kind;row.appendChild(tag);
            const body=document.createElement('span');
            body.textContent=args.map(a=>{try{return typeof a==='object'?JSON.stringify(a,null,2):String(a)}catch(e){return String(a)}}).join(' ');
            row.appendChild(body);out.appendChild(row);
          };
          ['log','info','warn','error'].forEach(k=>{const o=console[k];console[k]=(...a)=>{write(k,a);o&&o.apply(console,a)}});
          window.addEventListener('error',e=>write('error',[e.message]));
          window.addEventListener('unhandledrejection',e=>write('error',[String(e.reason&&e.reason.message||e.reason)]));
          const startedAt=performance.now();
          try{
            const result=(function(){ ${escaped} })();
            if(typeof result!=='undefined')write('result',[result]);
          }catch(e){write('error',[e&&e.message||String(e)])}
          const took=Math.round(performance.now()-startedAt);
          const foot=document.createElement('div');foot.className='row';foot.style.marginTop='10px';foot.style.opacity='0.5';
          foot.textContent='// finished in '+took+'ms';out.appendChild(foot);
        })();
      </script></body></html>`;
      setPreview(html);
    } else {
      toast.info("Live run is available for Python, HTML, CSS and JavaScript. For other languages, use 'Mentor Review'.");
    }
  };

  return (
    <div data-testid={CODING.root} className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono text-primary">// coding lab</div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tighter sm:text-4xl">
            Write. Get reviewed. Improve.
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Your mentor gives hints before answers, explains your mistakes, and tracks your growth.
          </p>
        </div>
        <Button data-testid={CODING.exerciseBtn} onClick={generateExercise} variant="outline" className="rounded-full font-mono text-xs uppercase tracking-widest">
          <Wand2 className="mr-2 h-4 w-4" /> Generate exercise
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {LANGS.map((l) => (
          <button
            key={l.key}
            data-testid={CODING.langSelect(l.key)}
            onClick={() => changeLang(l.key)}
            className={`rounded-full border px-4 py-1.5 font-mono text-xs uppercase tracking-widest transition ${lang === l.key ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40"}`}
          >
            {l.label}{l.key === "matlab" ? " · Mentor mode" : ""}
          </button>
        ))}
      </div>

      <div>
        <div className="label-mono">What are you trying to do? (optional)</div>
        <Input value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. Write a function that returns the nth Fibonacci number." className="mt-2 h-11 rounded-lg" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div data-testid={CODING.editor} className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="flex items-center justify-between border-b border-border px-4 py-2">
            <div className="label-mono text-primary">// {active?.label}</div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 label-mono cursor-pointer">
                <input type="checkbox" checked={hintsOnly} onChange={(e) => setHintsOnly(e.target.checked)} className="accent-primary" />
                hints-first
              </label>
            </div>
          </div>
          <CodeMirror
            value={code}
            onChange={(v) => setCode(v)}
            height="420px"
            theme={theme === "dark" ? oneDark : "light"}
            extensions={active?.ext ? [active.ext()] : []}
            basicSetup={{ lineNumbers: true, highlightActiveLine: true, autocompletion: true }}
          />
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border p-3">
            <div className="flex items-center gap-2">
              <Button data-testid={CODING.reviewBtn} onClick={doReview} disabled={reviewing} className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest">
                <Sparkles className="mr-2 h-4 w-4" /> {reviewing ? "Reviewing…" : "Mentor Review"}
              </Button>
              {lang === "python" && (
                <Button
                  data-testid="coding-run-python-btn"
                  onClick={runPython}
                  disabled={pyRunning}
                  variant="outline"
                  className="rounded-full font-mono text-xs uppercase tracking-widest border-accent/50 text-accent hover:bg-accent/10"
                >
                  {pyRunning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                  {pyRunning ? (pyStage === "script" ? "Fetching runtime…" : pyStage === "runtime" ? "Booting Python…" : "Running…") : "Run Python"}
                </Button>
              )}
              {["html", "css", "javascript"].includes(lang) && (
                <Button data-testid={CODING.runBtn} onClick={runPreview} variant="outline" className="rounded-full font-mono text-xs uppercase tracking-widest">
                  <Play className="mr-2 h-4 w-4" /> Run
                </Button>
              )}
            </div>
            <div className="label-mono">{code.split("\n").length} lines · {code.length} chars</div>
          </div>
        </div>

        <div data-testid={CODING.reviewOutput} className="rounded-2xl border border-border bg-card p-5">
          {!review && !preview && !pyOutput && (
            <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-border p-10 text-center">
              <div>
                <Sparkles className="mx-auto h-8 w-8 text-muted-foreground" />
                <div className="mt-2 text-sm text-muted-foreground">Write code and click <b>Mentor Review</b>.</div>
                <div className="mt-1 text-xs text-muted-foreground">Or click <b>Run Python</b> / <b>Run</b> to execute your code in the browser.</div>
              </div>
            </div>
          )}
          {pyOutput && (
            <div data-testid="coding-python-output" className="h-full flex flex-col">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2 label-mono text-accent">
                  <TerminalIcon className="h-3.5 w-3.5" /> python output
                </div>
                <button
                  onClick={() => setPyOutput(null)}
                  className="rounded-full border border-border px-2 py-0.5 font-mono text-[0.6rem] uppercase tracking-widest hover:border-primary"
                >
                  clear
                </button>
              </div>
              <pre className="flex-1 min-h-[380px] max-h-[500px] overflow-auto rounded-lg border border-border bg-background p-4 font-mono text-xs leading-relaxed whitespace-pre-wrap">
{pyOutput.status === "loading"
  ? "Loading Python runtime… (first run only)"
  : (pyOutput.lines.join("\n") || (pyOutput.error ? "" : "// no output — did your code print anything?"))}
{pyOutput.error && (
  "\n\n// error\n" + pyOutput.error
)}
              </pre>
              <div className="mt-2 label-mono">
                Running in-browser via Pyodide · {pyOutput.error ? "finished with errors" : pyOutput.status === "done" ? "finished" : "…"}
              </div>
            </div>
          )}
          {preview && (
            <div className="h-full">
              <div className="mb-3 label-mono">// preview</div>
              <iframe title="preview" srcDoc={preview} sandbox="allow-scripts" className="h-[420px] w-full rounded-lg border border-border bg-white" />
            </div>
          )}
          {review && !preview && !pyOutput && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {review.verdict === "correct" ? <CheckCircle2 className="h-5 w-5 text-accent" /> : <AlertTriangle className="h-5 w-5 text-primary" />}
                <div className="font-display text-lg font-bold capitalize">{review.verdict}</div>
              </div>

              {(review.mistakes || []).length > 0 && (
                <div>
                  <div className="label-mono mb-2">// mistakes</div>
                  <ul className="space-y-2">
                    {review.mistakes.map((m, i) => (
                      <li key={i} className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
                        <div className="font-medium">{m.issue} {m.line ? <span className="label-mono">line {m.line}</span> : null}</div>
                        <div className="mt-1 text-xs text-muted-foreground">{m.why}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(review.hints || []).length > 0 && (
                <div>
                  <div className="mb-2 flex items-center gap-2 label-mono"><Lightbulb className="h-3.5 w-3.5 text-primary" /> hints</div>
                  <ol className="space-y-2 pl-4">
                    {review.hints.map((h, i) => (<li key={i} className="text-sm list-decimal">{h}</li>))}
                  </ol>
                </div>
              )}

              {(review.suggested_improvements || []).length > 0 && (
                <div>
                  <div className="mb-2 flex items-center gap-2 label-mono"><TrendingUp className="h-3.5 w-3.5 text-accent" /> improvements</div>
                  <ul className="space-y-1 pl-4">
                    {review.suggested_improvements.map((s, i) => (<li key={i} className="text-sm list-disc">{s}</li>))}
                  </ul>
                </div>
              )}

              {review.next_practice && (
                <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
                  <div className="label-mono text-primary">// next practice</div>
                  <div className="mt-1 text-sm">{review.next_practice}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
